// @flow weak

import { connect } from 'react-redux';
import { flowRight, get, isFunction } from 'lodash';

import { InlineUpgradeTrigger } from '@zapier/editor';
import { hocify } from '@zapier/legacy-components';

import * as Account from 'app/entities/Account';
import ChangePlanContainer from 'app/billing/components/ChangePlanContainer';
import FlowActions from 'app/beta-editor/FlowActions';
import MaybeLoad from 'app/common/components/MaybeLoad';
import {
  selectNode,
  selectNodePlanValidation,
  selectNodeWithPlanLimits,
} from 'app/beta-editor/Selectors';

import type { Plan } from '@zapier/design-system-revenue';
import type { TrialExtensionReason } from '@zapier/billing';

import type { ID } from 'app/common/types';

type Props = {
  nodeId?: ID,
  flowId: ID,
  autoOpen?: boolean,
  upgradeToPlan?: Plan,
  reason?: TrialExtensionReason,
  onClose?: () => void,
};

const mapStateToProps = (state, ownProps: Props) => {
  const { flowId, nodeId } = ownProps;
  let node;
  let reason = ownProps.reason;
  let upgradeToPlan = ownProps.upgradeToPlan;
  const currentAccount = Account.selectors.all.currentAccount(state);

  // Triggering from node validation
  // otherwise, these props are provided by the user
  if (nodeId) {
    node = selectNode(state, { flowId, nodeId });
    const validation = selectNodePlanValidation(state, { flowId, nodeId });
    reason = get(validation, 'reason');
    upgradeToPlan =
      get(validation, ['plans', 'annual']) ||
      get(validation, ['plans', 'monthly']);
  } else {
    node = selectNodeWithPlanLimits(state, flowId);
  }

  const canExtendTrial =
    reason &&
    Account.selectors.isFree(currentAccount) &&
    !Account.selectors.hasExtendedTrialFor(reason, currentAccount);

  return {
    autoOpen: ownProps.autoOpen,
    appName: get(node, 'selected_api'),
    ChangePlanContainer,
    upgradeToPlan,
    validationReason: reason,
    canExtendTrial,
    didExtendTrial: Account.selectors.didExtendTrial(currentAccount),
  };
};

const mapDispatchToProps = (
  dispatch,
  { autoOpen, flowId, nodeId, onClose }: Props
) => ({
  onExtendTrial(reason) {
    dispatch(Account.actions.extendCurrentTrial(reason));
  },

  onUpgradeFinish() {
    // This is the only point in which we know the user has upgraded
    dispatch(
      FlowActions.clearNodeValidations({
        flowId,
        nodeId,
      })
    );

    // Attempt to unpause zap
    if (autoOpen && flowId) {
      dispatch(FlowActions.unpause(flowId));
    }
  },

  onClose() {
    if (isFunction(onClose)) {
      onClose();
    }
    dispatch(
      FlowActions.clearNodeValidationsOnTrialExtension({
        flowId,
        nodeId,
      })
    );
    dispatch(Account.actions.updateCurrentTrialExtensionState(false));
  },

  onLoad() {
    dispatch(Account.actions.loadCollection());
  },
});

export default flowRight(
  connect(mapStateToProps, mapDispatchToProps),
  hocify(MaybeLoad)
)(InlineUpgradeTrigger);
